import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import DashboardLayout from '@/layouts/DashboardLayout';
import LearnerLayout from '@/layouts/LearnerLayout';
import AuthLayout from '@/layouts/AuthLayout';
import { AuthProvider } from '@/contexts/AuthContext';
import { AuthGuard, GuestGuard } from '@/components/auth/AuthGuard';

import RouteGuard from '@/components/RouteGuard';
import ErrorBoundary from '@/components/ErrorBoundary';
import RouteErrorElement from '@/components/RouteErrorElement';
import RouteTracker from '@/components/analytics/RouteTracker';
import Login from '@/pages/auth/Login';
import Register from '@/pages/auth/Register';
import RegisterInvite from '@/pages/auth/RegisterInvite';
import ForgotPassword from '@/pages/auth/ForgotPassword';
import ResetPassword from '@/pages/auth/ResetPassword';
import VerifyEmail from '@/pages/auth/VerifyEmail';
import UnauthorizedAccess from '@/pages/errors/UnauthorizedAccess';
import AccessDenied from '@/pages/errors/AccessDenied';
import TenantBlocked from '@/pages/errors/TenantBlocked';
import Dashboard from '@/pages/Dashboard';
import LearnerDashboard from '@/pages/learner/LearnerDashboard';
import LearnerCourses from '@/pages/learner/LearnerCourses';
import LearnerTickets from '@/pages/learner/LearnerTickets';
import CreateTicket from '@/pages/learner/CreateTicket';
import TicketChat from '@/pages/learner/TicketChat';
import LearnerLearningPaths from '@/pages/learner/LearnerLearningPaths';
import LearningPathDetailPage from '@/pages/learner/LearningPathDetail';
import LearnerProgress from '@/pages/learner/LearnerProgress';
import LearnerAchievements from '@/pages/learner/LearnerAchievements';
import GamificationBadges from '@/pages/learner/GamificationBadges';
import LearnerSchedule from '@/pages/learner/LearnerSchedule';
import LearnerCertificates from '@/pages/learner/LearnerCertificates';
import LearnerCertifications from '@/pages/learner/LearnerCertifications';
import CertificationPreview from '@/pages/learner/CertificationPreview';
import LearnerExams from '@/pages/learner/LearnerExams';
import LearnerStartExam from '@/pages/learner/LearnerStartExam';
import LearnerDoExam from '@/pages/learner/LearnerDoExam';
import ExamSuccess from '@/pages/learner/ExamSuccess';
import LearnerExamResults from '@/pages/learner/LearnerExamResults';
import Forums from '@/pages/learner/Forums';
import StudyGroups from '@/pages/learner/StudyGroups';
import CareerPath from '@/pages/learner/CareerPath';
import LeaderboardPage from '@/pages/learner/Leaderboard';
import Skills from '@/pages/learner/Skills';
import Library from '@/pages/learner/Library';
import Downloads from '@/pages/learner/Downloads';
import LearnerAssessments from '@/pages/learner/LearnerAssessments';
import HelpCenter from '@/pages/learner/HelpCenter';
import SmartRedirect from '@/components/auth/SmartRedirect';
import SentryCheck from '@/pages/__sentry-check'; // TEMP: remove after Sentry verification
import CourseCatalog from '@/pages/courses/CourseCatalog';
import AddCourse from '@/pages/courses/AddCourse';
import CreateManualCourse from '@/pages/courses/CreateManualCourse';
import ScormUploadPage from '@/pages/scorm/ScormUploadPage';
import PrivateAvatarLibrary from '@/pages/avatars/PrivateAvatarLibrary';
import Users from '@/pages/users/Users';
import UserCourseList from '@/pages/users/UserCourseList';
import UserCertificationList from '@/pages/users/UserCertificationList';
import UserCertificationProgress from '@/pages/users/UserCertificationProgress';
import Departments from '@/pages/users/Groups';
import Roles from '@/pages/users/Roles';
import GroupCourseList from '@/pages/groups/GroupCourseList';
import RoleCourseList from '@/pages/roles/RoleCourseList';
import AssignPermissionToRole from '@/pages/roles/AssignPermissionToRole';
import Settings from '@/pages/settings/Settings';
import Analytics from '@/pages/analytics/Analytics';
import TrainingStatusReport from '@/pages/training-status-report/TrainingStatusReport';
import CoursePerformance from '@/pages/analytics/CoursePerformance';
import LearningPathPerformance from '@/pages/analytics/LearningPathPerformance';
import EmployeeProgress from '@/pages/analytics/EmployeeProgress';
import MyTeamProgress from '@/pages/analytics/MyTeamProgress';
import DepartmentProgress from '@/pages/analytics/DepartmentProgress';
import Certifications from '@/pages/certifications/Certifications';
import CreateCertification from '@/pages/certifications/CreateCertification';
import CreateManualCertification from '@/pages/certifications/CreateManualCertification';
import CertificationDetails from '@/pages/certifications/CertificationDetails';
import CertificationGenerationStatus from '@/pages/certifications/CertificationGenerationStatus';
import ExamEditor from '@/pages/certifications/ExamEditor';
import CertificationAssignment from '@/pages/certifications/CertificationAssignment';
import CertificationAssignmentAnalytics from '@/pages/certifications/CertificationAssignmentAnalytics';
import LearningPaths from '@/pages/learning-paths/LearningPaths';
import CreateLearningPath from '@/pages/learning-paths/CreateLearningPath';
import LearningPathGenerationStatus from '@/pages/learning-paths/LearningPathGenerationStatus';
import EditLearningPath from '@/pages/learning-paths/EditLearningPath';
import LearningPathDetails from '@/pages/learning-paths/LearningPathDetails';
import WorkflowList from '@/pages/learning-path-workflows/WorkflowList';
import WorkflowCreate from '@/pages/learning-path-workflows/WorkflowCreate';
import WorkflowStatus from '@/pages/learning-path-workflows/WorkflowStatus';
import Assessments from '@/pages/assessments/Assessments';
import Leaderboard from '@/pages/gamification/Leaderboard';
import Achievements from '@/pages/gamification/Achievements';
import Rewards from '@/pages/gamification/Rewards';
import BadgeManagement from '@/pages/gamification/BadgeManagement';
import UserAchievements from '@/pages/gamification/UserAchievements';
import UserAchievementDetail from '@/pages/gamification/UserAchievementDetail';
import CourseAssignment from '@/pages/courses/CourseAssignment';
import CourseAssignmentAnalytics from '@/pages/courses/CourseAssignmentAnalytics';
import UserCourseProgress from '@/pages/courses/UserCourseProgress';
import CourseDetails from '@/pages/courses/CourseDetails';
import Profile from '@/pages/Profile';
import LearningPathAssignment from '@/pages/learningPaths/LearningPathAssignment';
import LearningPathAssignmentAnalytics from '@/pages/learningPaths/LearningPathAssignmentAnalytics';
import HandleToken from '@/pages/auth/HandleToken';
import LtiContentSelect from '@/pages/lti/LtiContentSelect';
import LtiSignedOut from '@/pages/lti/LtiSignedOut';
import ActivateAccount from '@/pages/auth/ActivateAccount';
import InvalidInvitationPage from '@/components/auth/InvalidInvitationPage';
import InvitationGuard from '@/components/auth/InvitationGuard';
import CoursePreview from '@/pages/courses/CoursePreview';
import AILearnPage from '@/pages/courses/AILearnPage';
import CoursePlayer from '@/pages/courses/CoursePlayer';
import BuildCourse from '@/pages/courses/BuildCourse';
import CourseTracking from '@/pages/courses/CourseTracking';
import EditCourse from '@/pages/courses/EditCourse';
import TrendingCourseList from '@/pages/reports/TrendingCourseList';
import SSOConfiguration from '@/pages/enterprise/SSOConfiguration';
import AddSSOProvider from '@/pages/enterprise/AddSSOProvider';
import CreditPlans from '@/pages/enterprise/CreditPlans';
import CreditPlanFormPage from '@/pages/enterprise/CreditPlanFormPage';
import TenantCreditPlans from '@/pages/enterprise/TenantCreditPlans';
import LtiIntegrations from '@/pages/enterprise/LtiIntegrations';
import AddLtiPlatform from '@/pages/enterprise/AddLtiPlatform';
import Categories from '@/pages/knowledge/Categories';
import Documents from '@/pages/knowledge/Documents';
import DocumentStoreDetail from '@/pages/knowledge/DocumentStoreDetail';
import ManageDocuments from '@/pages/knowledge/ManageDocuments';
import BusinessSettings from '@/pages/settings/BusinessSettings';
import AIModelSettings from '@/pages/settings/AIModelSettings';
import WebhookSettings from '@/pages/settings/WebhookSettings';
import ScimConfiguration from '@/pages/settings/ScimConfiguration';
import RedisCacheMonitor from '@/pages/settings/RedisCacheMonitor';
import AdminTicketRequests from '@/pages/settings/AdminTicketRequests';
import AdminTicketDetail from '@/pages/settings/AdminTicketDetail';
import Reports from '@/pages/reports/Reports';
import ActivityReport from '@/pages/reports/ActivityReport';
import TenantReports from '@/pages/reports/TenantReports';
import AiCostTracking from '@/pages/reports/AiCostTracking';
import TenantAiUsage from '@/pages/reports/TenantAiUsage';
import InviteManagement from '@/pages/users/InviteManagement';
import LiveChat from '@/pages/learn/LiveChat';
import StreakLeaderboard from '@/pages/learner/StreakLeaderboard';
import MyReviews from '@/pages/learner/MyReviews';
// Reporting V2 - Dashboards
import AdminOverview from '@/pages/reports/dashboards/AdminOverview';
import ExecutiveSummary from '@/pages/reports/dashboards/ExecutiveSummary';
import ManagerTeam from '@/pages/reports/dashboards/ManagerTeam';
import LearnerProgressDashboard from '@/pages/reports/dashboards/LearnerProgress';
import DepartmentPerformance from '@/pages/reports/dashboards/DepartmentPerformance';
import CourseAnalytics from '@/pages/reports/dashboards/CourseAnalytics';
// Reporting V2 - Reports
import GenerateReport from '@/pages/reports/GenerateReport';
import SavedReports from '@/pages/reports/SavedReports';
import ReportDetail from '@/pages/reports/ReportDetail';
// Reporting V2 - Schedules
import SchedulesList from '@/pages/reports/schedules/SchedulesList';
import ScheduleForm from '@/pages/reports/schedules/ScheduleForm';
import ScheduleDetail from '@/pages/reports/schedules/ScheduleDetail';
// Reporting V2 - Subscriptions
import SubscriptionsList from '@/pages/reports/subscriptions/SubscriptionsList';
import SubscriptionForm from '@/pages/reports/subscriptions/SubscriptionForm';
import SubscriptionDetail from '@/pages/reports/subscriptions/SubscriptionDetail';
import CourseUsers from './pages/analytics/Courseuser';
import LearningPathUsers from './pages/analytics/LearningPathUsers';
import LearningPathCourseUsers from './pages/analytics/LearningPathCourseUsers';
import UserLearningPathProgress from './pages/analytics/UserLearningPathProgress';
import RoleProgress from './pages/analytics/RoleProgress';
import LeaderboardLearn from '@/pages/learner/Leaderboard';
import TenantsList from '@/pages/tenants/TenantsList';
import TenantFormPage from '@/pages/tenants/TenantFormPage';
import ManagementOnlyRoute from '@/components/auth/ManagementOnlyRoute';
import CertificationPerformance from '@/pages/analytics/CertificationPerformance';
import CertificationUsers from '@/pages/analytics/CertificationUsers';
import EmailTemplateList from '@/pages/email-templates/EmailTemplateList';
import EmailTemplateEditorV1 from '@/pages/email-templates/EmailTemplateEditorV1';
import EmailTemplateEditor from '@/pages/email-templates/EmailTemplateEditor';
import CertificateTemplateList from '@/pages/certificate-templates/CertificateTemplateList';
import CertificateTemplateEditor from '@/pages/certificate-templates/CertificateTemplateEditor';
import CertificateTemplateBuilder from '@/pages/certificate-templates/CertificateTemplateBuilder';
import CertificateVerify from '@/pages/public/CertificateVerify';
import ReviewsManagement from '@/pages/reviews/ReviewsManagement';
import AuditTrailReport from '@/pages/reports/AuditTrailReport';
import EmailLogPage from '@/pages/email-log';
import AdminNotificationsPage from '@/pages/admin/notifications';
import LearnerNotificationsPage from '@/pages/learner/notifications';
import LearnerNotificationSettingsPage from '@/pages/learner/notifications/settings';
import ErrorPage from '@/components/ErrorPage';
import SkillzenDocs from '@/docs/SkillzenDocs';
import { IndexRedirect } from '@/components/auth/SmartRedirect';
import { DASHBOARD_PERMISSION, LEARNER_DASHBOARD_PERMISSION } from '@/constants/constant-permissions';
import {
  // Admin
  ADMIN_DASHBOARD,
  ADMIN_COURSE_CATALOG,
  ADMIN_COURSE_ADD,
  ADMIN_COURSE_CREATE_MANUAL,
  ADMIN_SCORM_UPLOAD,
  ADMIN_AVATAR_LIBRARY,
  ADMIN_COURSE_BUILD,
  ADMIN_COURSE_CATEGORIES,
  ADMIN_COURSE_ASSIGNMENT,
  ADMIN_COURSE_ASSIGNMENT_ANALYTICS,
  ADMIN_USERS,
  ADMIN_USERS_DEPARTMENTS,
  ADMIN_USERS_INVITATIONS,
  ADMIN_ROLES,
  ADMIN_ROLE_ASSIGN_PERMISSIONS,
  ADMIN_TENANTS,
  ADMIN_TENANT_CREATE,
  ADMIN_TENANT_EDIT,
  ADMIN_SETTINGS,
  ADMIN_SETTINGS_BUSINESS,
  ADMIN_SETTINGS_TICKET_REQUESTS,
  ADMIN_SETTINGS_WEBHOOKS,
  ADMIN_SETTINGS_REDIS_CACHE,
  ADMIN_SETTINGS_AI_MODELS,
  ADMIN_TRAINING_STATUS_REPORT,
  ADMIN_ANALYTICS,
  ADMIN_ANALYTICS_COURSE_PERFORMANCE,
  ADMIN_ANALYTICS_LEARNING_PATH_PERFORMANCE,
  ADMIN_ANALYTICS_CERTIFICATION_PERFORMANCE,
  ADMIN_ANALYTICS_EMPLOYEE_PROGRESS,
  ADMIN_ANALYTICS_MY_TEAM_PROGRESS,
  ADMIN_ANALYTICS_DEPARTMENT_PROGRESS,
  ADMIN_ANALYTICS_ROLE_PROGRESS,
  ADMIN_CERTIFICATIONS,
  ADMIN_CERTIFICATION_CREATE,
  ADMIN_CERTIFICATION_CREATE_MANUAL,
  ADMIN_CERTIFICATION_ASSIGNMENT,
  ADMIN_CERTIFICATION_ASSIGNMENT_ANALYTICS,
  ADMIN_LEARNING_PATHS,
  ADMIN_LEARNING_PATH_CREATE,
  ADMIN_LP_WORKFLOW_LIST,
  ADMIN_LP_WORKFLOW_CREATE,
  ADMIN_LEARNING_PATH_ASSIGNMENT,
  ADMIN_LEARNING_PATH_ASSIGNMENT_ANALYTICS,
  ADMIN_ASSESSMENTS,
  ADMIN_LEADERBOARD,
  ADMIN_GAMIFICATION_ACHIEVEMENTS,
  ADMIN_GAMIFICATION_REWARDS,
  ADMIN_GAMIFICATION_BADGES,
  ADMIN_GAMIFICATION_USER_ACHIEVEMENTS,
  ADMIN_PROFILE,
  ADMIN_LIVE_CHAT,
  ADMIN_REPORTS,
  ADMIN_REPORTS_ACTIVITY,
  ADMIN_REPORTS_TRENDING,
  ADMIN_REPORTS_AUDIT_TRAIL,
  ADMIN_REPORTS_TENANT,
  ADMIN_REPORTS_AI_COST,
  ADMIN_AI_USAGE,
  ADMIN_REPORTING_ADMIN_OVERVIEW,
  ADMIN_REPORTING_EXECUTIVE_SUMMARY,
  ADMIN_REPORTING_MANAGER_TEAM,
  ADMIN_REPORTING_LEARNER_PROGRESS,
  ADMIN_REPORTING_DEPARTMENT_PERFORMANCE,
  ADMIN_REPORTING_COURSE_ANALYTICS,
  ADMIN_REPORTING_GENERATE,
  ADMIN_REPORTING_SAVED,
  ADMIN_REPORTING_SCHEDULES,
  ADMIN_REPORTING_SCHEDULE_CREATE,
  ADMIN_REPORTING_SUBSCRIPTIONS,
  ADMIN_REPORTING_SUBSCRIPTION_CREATE,
  ADMIN_CREDIT_PLANS,
  ADMIN_CREDIT_PLAN_CREATE,
  ADMIN_CREDIT_PLAN_EDIT,
  ADMIN_TENANT_CREDIT_PLANS,
  ADMIN_SSO_CONFIGURATION,
  ADMIN_SSO_ADD,
  ADMIN_SCIM_CONFIGURATION,
  ADMIN_LTI_INTEGRATIONS,
  ADMIN_LTI_ADD,
  ADMIN_LTI_EDIT,
  ADMIN_EMAIL_TEMPLATES,
  ADMIN_EMAIL_TEMPLATE_CREATE,
  ADMIN_CERTIFICATE_TEMPLATES,
  ADMIN_CERTIFICATE_TEMPLATE_CREATE,
  ADMIN_KNOWLEDGE_DOCUMENTS,
  ADMIN_KNOWLEDGE_MANAGE_DOCUMENTS,
  ADMIN_REVIEWS,
  ADMIN_EMAIL_LOGS,
  ADMIN_NOTIFICATIONS,
  ADMIN_NOTIFICATIONS_SETTINGS,
  ADMIN_MY_NOTIFICATIONS,
  ADMIN_NOTIFICATION_SUBSCRIBERS,
  // Learner
  LEARNER_DASHBOARD,
  LEARNER_COURSES,
  LEARNER_LEARNING_PATHS,
  LEARNER_LIVE_CHAT,
  LEARNER_PROGRESS,
  LEARNER_ACHIEVEMENTS,
  LEARNER_MY_ACHIEVEMENTS,
  LEARNER_LEADERBOARD,
  LEARNER_STREAK_LEADERBOARD,
  LEARNER_SCHEDULE,
  LEARNER_CERTIFICATES,
  LEARNER_CERTIFICATIONS,
  LEARNER_EXAMS,
  LEARNER_FORUMS,
  LEARNER_STUDY_GROUPS,
  LEARNER_CAREER,
  LEARNER_SKILLS,
  LEARNER_LIBRARY,
  LEARNER_DOWNLOADS,
  LEARNER_ASSESSMENTS,
  LEARNER_HELP,
  LEARNER_MY_REVIEWS,
  LEARNER_PROFILE,
  LEARNER_TICKETS,
  LEARNER_TICKET_CREATE,
  LEARNER_NOTIFICATIONS,
  LEARNER_NOTIFICATIONS_SETTINGS,
  // Auth
  AUTH_LOGIN,
  AUTH_REGISTER,
  AUTH_REGISTER_INVITE,
  AUTH_FORGOT_PASSWORD,
  AUTH_RESET_PASSWORD,
  AUTH_VERIFY_EMAIL,
  AUTH_HANDLE_TOKEN,
  LTI_CONTENT_SELECT,
  LTI_SIGNED_OUT,
  AUTH_ACTIVATE_ACCOUNT,
  // Error
  ERROR_UNAUTHORIZED,
  ERROR_ACCESS_DENIED,
  ERROR_TENANT_BLOCKED,
  ERROR_INVALID_INVITATION,
  // Public
  PUBLIC_CERTIFICATE_VERIFY,
} from '@/constants/constant-routes';


function RootLayout() {
  return (
    <AuthProvider>
      <RouteTracker />
    </AuthProvider>
  );
}

export const router = createBrowserRouter([
  {
    // Root route with AuthProvider and RouteTracker
    element: <RootLayout />,
    // Root-level errorElement: errors in any descendant route bubble up here,
    // replacing React Router's default error UI (which leaks message + stack
    // trace to end users — VAPT May 2026, finding #18).
    errorElement: <RouteErrorElement />,
    children: [
      // Root and dashboard redirects (must be before specific routes)
      {
        path: '/',
        element: (
          <AuthGuard>
            <SmartRedirect />
          </AuthGuard>
        ),
      },
      {
        path: '/dashboard',
        element: (
          <AuthGuard>
            <SmartRedirect />
          </AuthGuard>
        ),
      },

      // Documentation (standalone — not inside DashboardLayout)
      {
        path: '/docs/:moduleType?',
        element: (
           <SkillzenDocs />

        ),
      },

      // TEMP: Sentry verification page. No auth, no layout. Delete after verify.
      {
        path: '/__sentry-check',
        element: <SentryCheck />,
      },

      // Admin Routes
  {
    path: '/admin',
    element: (
      <AuthGuard>
        <RouteGuard requiredRole="admin">
          <DashboardLayout />
        </RouteGuard>
      </AuthGuard>
    ),
    children: [
      { index: true, element: <IndexRedirect requiredPermission={DASHBOARD_PERMISSION.DASHBOARD_READ} defaultRoute={ADMIN_DASHBOARD} routePrefix="/admin" /> },
      { path: ADMIN_DASHBOARD, element: <Dashboard /> },
      { path: ADMIN_COURSE_CATALOG, element: <CourseCatalog /> },
      { path: ADMIN_COURSE_ADD, element: <AddCourse /> },
      { path: ADMIN_COURSE_CREATE_MANUAL, element: <CreateManualCourse /> },
      { path: ADMIN_SCORM_UPLOAD, element: <ScormUploadPage /> },
      { path: ADMIN_AVATAR_LIBRARY, element: <PrivateAvatarLibrary /> },
      { path: ADMIN_USERS, element: <Users /> },
      { path: 'users/:userId/courses', element: <UserCourseList /> },
      { path: 'users/:userId/certifications', element: <UserCertificationList /> },
      { path: 'users/:userId/certifications/:certificationId/progress', element: <UserCertificationProgress /> },
      { path: ADMIN_USERS_DEPARTMENTS, element: <Departments /> },
      { path: ADMIN_USERS_INVITATIONS, element: <InviteManagement /> },
      { path: 'groups/:groupKey/courses', element: <GroupCourseList /> },
      { path: ADMIN_ROLES, element: <Roles /> },
      { path: 'roles/:roleKey/courses', element: <RoleCourseList /> },
      { path: ADMIN_ROLE_ASSIGN_PERMISSIONS, element: <AssignPermissionToRole /> },
      { path: 'roles/:roleKey/permissions', element: <AssignPermissionToRole /> },
      { path: ADMIN_TENANTS, element: <ManagementOnlyRoute><TenantsList /></ManagementOnlyRoute> },
      { path: ADMIN_CREDIT_PLANS, element: <ManagementOnlyRoute><CreditPlans /></ManagementOnlyRoute> },
      { path: ADMIN_CREDIT_PLAN_CREATE, element: <ManagementOnlyRoute><CreditPlanFormPage /></ManagementOnlyRoute> },
      { path: ADMIN_CREDIT_PLAN_EDIT, element: <ManagementOnlyRoute><CreditPlanFormPage /></ManagementOnlyRoute> },
      { path: ADMIN_TENANT_CREDIT_PLANS, element: <ManagementOnlyRoute><TenantCreditPlans /></ManagementOnlyRoute> },
      { path: ADMIN_TENANT_CREATE, element: <ManagementOnlyRoute><TenantFormPage /></ManagementOnlyRoute> },
      { path: ADMIN_TENANT_EDIT, element: <ManagementOnlyRoute><TenantFormPage /></ManagementOnlyRoute> },
      { path: ADMIN_SETTINGS, element: <Settings /> },
      { path: ADMIN_SETTINGS_BUSINESS, element: <BusinessSettings /> },
      { path: ADMIN_SETTINGS_TICKET_REQUESTS, element: <AdminTicketRequests /> },
      { path: 'settings/ticket-requests/:ticketId', element: <AdminTicketDetail /> },
      { path: ADMIN_SETTINGS_WEBHOOKS, element: <WebhookSettings /> },
      { path: ADMIN_SETTINGS_REDIS_CACHE, element: <RedisCacheMonitor /> },
      { path: ADMIN_SETTINGS_AI_MODELS, element: <AIModelSettings /> },
      { path: ADMIN_TRAINING_STATUS_REPORT, element: <TrainingStatusReport /> },
      { path: ADMIN_ANALYTICS, element: <Analytics /> },
      { path: ADMIN_ANALYTICS_COURSE_PERFORMANCE, element: <CoursePerformance /> },
      { path: ADMIN_ANALYTICS_LEARNING_PATH_PERFORMANCE, element: <LearningPathPerformance /> },
      { path: ADMIN_ANALYTICS_CERTIFICATION_PERFORMANCE, element: <CertificationPerformance /> },
      { path: ADMIN_ANALYTICS_EMPLOYEE_PROGRESS, element: <EmployeeProgress /> },
      { path: ADMIN_ANALYTICS_MY_TEAM_PROGRESS, element: <MyTeamProgress /> },
      { path: ADMIN_ANALYTICS_DEPARTMENT_PROGRESS, element: <DepartmentProgress /> },
      { path: ADMIN_ANALYTICS_ROLE_PROGRESS, element: <RoleProgress /> },
      { path: ADMIN_CERTIFICATIONS, element: <Certifications /> },
      { path: ADMIN_CERTIFICATION_CREATE, element: <CreateCertification /> },
      { path: ADMIN_CERTIFICATION_CREATE_MANUAL, element: <CreateManualCertification /> },
      { path: 'certifications/generate/:id', element: <CertificationGenerationStatus /> },
      { path: 'certifications/:id', element: <CertificationDetails /> },
      { path: 'certifications/:id/exam', element: <ExamEditor /> },
      { path: ADMIN_CERTIFICATION_ASSIGNMENT, element: <CertificationAssignment /> },
      { path: ADMIN_CERTIFICATION_ASSIGNMENT_ANALYTICS, element: <CertificationAssignmentAnalytics /> },
      { path: ADMIN_LEARNING_PATHS, element: <LearningPaths /> },
      { path: ADMIN_LEARNING_PATH_CREATE, element: <CreateLearningPath /> },
      { path: 'learning-paths/generate/:orchestration_id', element: <LearningPathGenerationStatus /> },
      { path: 'learning-paths/:id', element: <EditLearningPath /> },
      { path: ADMIN_LP_WORKFLOW_LIST, element: <WorkflowList /> },
      { path: ADMIN_LP_WORKFLOW_CREATE, element: <WorkflowCreate /> },
      { path: 'learning-paths/workflow/:id/edit', element: <WorkflowCreate /> },
      { path: 'learning-paths/workflow/:id', element: <WorkflowStatus /> },
      { path: ADMIN_LEARNING_PATH_ASSIGNMENT, element: <LearningPathAssignment /> },
      { path: ADMIN_LEARNING_PATH_ASSIGNMENT_ANALYTICS, element: <LearningPathAssignmentAnalytics /> },
      { path: ADMIN_ASSESSMENTS, element: <Assessments /> },
      { path: ADMIN_LEADERBOARD, element: <Leaderboard /> },
      { path: ADMIN_GAMIFICATION_ACHIEVEMENTS, element: <Achievements /> },
      { path: ADMIN_GAMIFICATION_REWARDS, element: <Rewards /> },
      { path: ADMIN_GAMIFICATION_BADGES, element: <BadgeManagement /> },
      { path: ADMIN_GAMIFICATION_USER_ACHIEVEMENTS, element: <UserAchievements /> },
      { path: 'gamification/user-achievements/:userId', element: <UserAchievementDetail /> },
      { path: ADMIN_COURSE_ASSIGNMENT, element: <CourseAssignment /> },
      { path: ADMIN_COURSE_ASSIGNMENT_ANALYTICS, element: <CourseAssignmentAnalytics /> },
      { path: 'course/assignments/user/:userId/course/:courseId/progress', element: <UserCourseProgress /> },
      { path: 'course/details/:id', element: <CourseDetails /> },
      { path: ADMIN_PROFILE, element: <Profile /> },
      { path: 'course/preview/:id', element: <CoursePreview /> },
      { path: 'certification/preview/:id', element: <CertificationPreview /> },
      { path: 'learning-paths/preview/:id', element: <LearningPathDetails /> },
      { path: 'ai-learn/:courseId', element: <AILearnPage /> },
      { path: ADMIN_LIVE_CHAT, element: <LiveChat /> },
      { path: 'live-chat/:conversationId', element: <LiveChat /> },
      { path: 'course-player/:courseId', element: <CoursePlayer /> },
      { path: ADMIN_COURSE_BUILD, element: <BuildCourse /> },
      { path: 'course-tracking/:courseId', element: <CourseTracking /> },
      { path: 'edit-course/:id', element: <EditCourse /> },
     { path: 'course-performance/:courseId/users', element: <CourseUsers /> },
     { path: 'learning-path-performance/:learningPathId/courses/users', element: <LearningPathCourseUsers /> },
     { path: 'learning-paths/:learningPathId/users/:userId/progress', element: <UserLearningPathProgress /> },
     { path: 'certification-performance/:certificationId/performance', element: <CertificationUsers /> },
    { path: ADMIN_REPORTS, element: <Reports /> },
      { path: ADMIN_REPORTS_ACTIVITY, element: <ActivityReport /> },
      { path: ADMIN_REPORTS_TRENDING, element: <TrendingCourseList /> },
      { path: ADMIN_REPORTS_AUDIT_TRAIL, element: <AuditTrailReport /> },
      // Tenant Reports & AI Cost Tracking — management-only cross-tenant dashboards.
      { path: ADMIN_REPORTS_TENANT, element: <ManagementOnlyRoute><TenantReports /></ManagementOnlyRoute> },
      { path: ADMIN_REPORTS_AI_COST, element: <ManagementOnlyRoute><AiCostTracking /></ManagementOnlyRoute> },
      // Tenant AI Usage — tenant-scoped (each tenant sees its own usage). Not management-only.
      { path: ADMIN_AI_USAGE, element: <TenantAiUsage /> },
      // Reporting V2 Routes - Dashboards
      { path: ADMIN_REPORTING_ADMIN_OVERVIEW, element: <AdminOverview /> },
      { path: ADMIN_REPORTING_EXECUTIVE_SUMMARY, element: <ExecutiveSummary /> },
      { path: ADMIN_REPORTING_MANAGER_TEAM, element: <ManagerTeam /> },
      { path: ADMIN_REPORTING_LEARNER_PROGRESS, element: <LearnerProgressDashboard /> },
      { path: ADMIN_REPORTING_DEPARTMENT_PERFORMANCE, element: <DepartmentPerformance /> },
      { path: ADMIN_REPORTING_COURSE_ANALYTICS, element: <CourseAnalytics /> },
      // Reporting V2 Routes - Reports
      { path: ADMIN_REPORTING_GENERATE, element: <GenerateReport /> },
      { path: ADMIN_REPORTING_SAVED, element: <SavedReports /> },
      { path: 'reporting/reports/:snapshotId', element: <ReportDetail /> },
      // Reporting V2 Routes - Schedules
      { path: ADMIN_REPORTING_SCHEDULES, element: <SchedulesList /> },
      { path: ADMIN_REPORTING_SCHEDULE_CREATE, element: <ScheduleForm /> },
      { path: 'reporting/schedules/:scheduleId', element: <ScheduleDetail /> },
      { path: 'reporting/schedules/:scheduleId/edit', element: <ScheduleForm /> },
      // Reporting V2 Routes - Subscriptions
      { path: ADMIN_REPORTING_SUBSCRIPTIONS, element: <SubscriptionsList /> },
      { path: ADMIN_REPORTING_SUBSCRIPTION_CREATE, element: <SubscriptionForm /> },
      { path: 'reporting/subscriptions/:subscriptionId', element: <SubscriptionDetail /> },
      { path: 'reporting/subscriptions/:subscriptionId/edit', element: <SubscriptionForm /> },
      { path: ADMIN_SSO_CONFIGURATION, element: <SSOConfiguration /> },
      { path: ADMIN_SSO_ADD, element: <AddSSOProvider /> },
      { path: 'enterprise/sso-configuration/edit/:id', element: <AddSSOProvider /> },
      { path: ADMIN_SCIM_CONFIGURATION, element: <ScimConfiguration /> },
      { path: ADMIN_LTI_INTEGRATIONS, element: <LtiIntegrations /> },
      { path: ADMIN_LTI_ADD, element: <AddLtiPlatform /> },
      { path: `${ADMIN_LTI_EDIT}/:id`, element: <AddLtiPlatform /> },
      { path: ADMIN_EMAIL_TEMPLATES, element: <EmailTemplateList /> },
      { path: ADMIN_EMAIL_TEMPLATE_CREATE, element: <EmailTemplateEditorV1 /> },
      { path: 'enterprise/email-templates/:id/edit', element: <EmailTemplateEditorV1 /> },
      { path: 'enterprise/email-templates/:id/visual-editor', element: <EmailTemplateEditor /> },
      { path: ADMIN_CERTIFICATE_TEMPLATES, element: <CertificateTemplateList /> },
      { path: ADMIN_CERTIFICATE_TEMPLATE_CREATE, element: <CertificateTemplateBuilder /> },
      { path: 'enterprise/certificate-templates/:id/edit', element: <CertificateTemplateBuilder /> },
      { path: ADMIN_COURSE_CATEGORIES, element: <Categories /> },
      { path: ADMIN_KNOWLEDGE_DOCUMENTS, element: <Documents /> },
      { path: 'knowledge/documents/:storeId', element: <DocumentStoreDetail /> },
      { path: ADMIN_KNOWLEDGE_MANAGE_DOCUMENTS, element: <ManageDocuments /> },
      { path: ADMIN_REVIEWS, element: <ReviewsManagement /> },
      { path: ADMIN_EMAIL_LOGS, element: <EmailLogPage /> },
      // Notification Management
      { path: ADMIN_NOTIFICATIONS, element: <AdminNotificationsPage /> },
      { path: ADMIN_NOTIFICATIONS_SETTINGS, element: <AdminNotificationsPage /> },
      { path: ADMIN_MY_NOTIFICATIONS, element: <AdminNotificationsPage /> },
      { path: ADMIN_NOTIFICATION_SUBSCRIBERS, element: <AdminNotificationsPage /> },
    ],
  },

  // Learner Routes
  {
    path: '/learn',
    element: (
      <AuthGuard>
        <RouteGuard requiredRole="learner">
          <LearnerLayout />
        </RouteGuard>
      </AuthGuard>
    ),
    children: [
      { index: true, element: <IndexRedirect requiredPermission={LEARNER_DASHBOARD_PERMISSION.LEARNER_DASHBOARD_READ} defaultRoute={LEARNER_DASHBOARD} routePrefix="/learn" /> },
      { path: LEARNER_DASHBOARD, element: <LearnerDashboard /> },
      { path: LEARNER_COURSES, element: <LearnerCourses /> },
      { path: 'course/:id', element: <CoursePreview isLearner={true} /> },
      { path: LEARNER_LEARNING_PATHS, element: <LearnerLearningPaths /> },
      { path: 'learning-paths/:learning_path_id', element: <LearningPathDetailPage isLearner={true} /> },
      { path: 'study/:courseId', element: <AILearnPage isLearner={true} /> },
      { path: LEARNER_LIVE_CHAT, element: <LiveChat /> },
      { path: 'live-chat/:conversationId', element: <LiveChat /> },
      { path: LEARNER_PROGRESS, element: <LearnerProgress /> },
      { path: LEARNER_ACHIEVEMENTS, element: <LearnerAchievements /> },
      { path: LEARNER_MY_ACHIEVEMENTS, element: <GamificationBadges /> },
      { path: LEARNER_LEADERBOARD, element: <LeaderboardLearn /> },
      { path: LEARNER_SCHEDULE, element: <LearnerSchedule /> },
      { path: LEARNER_CERTIFICATES, element: <LearnerCertificates /> },
      { path: LEARNER_CERTIFICATIONS, element: <LearnerCertifications /> },
      { path: 'certification/:id', element: <CertificationPreview isLearner={true} /> },
      { path: LEARNER_EXAMS, element: <LearnerExams /> },
      { path: 'exams/start/:examId', element: <LearnerStartExam /> },
      { path: 'exams/do-exam/:examId', element: <LearnerDoExam /> },
      { path: 'exams/success/:examId', element: <ExamSuccess /> },
      { path: 'exams/results/:examId', element: <LearnerExamResults /> },
      // Implemented routes
      { path: LEARNER_FORUMS, element: <Forums /> },
      { path: LEARNER_STUDY_GROUPS, element: <StudyGroups /> },
      { path: LEARNER_CAREER, element: <CareerPath /> },
      { path: LEARNER_LEADERBOARD, element: <LeaderboardPage /> },
      { path: LEARNER_STREAK_LEADERBOARD, element: <StreakLeaderboard /> },
      { path: LEARNER_SKILLS, element: <Skills /> },
      { path: LEARNER_LIBRARY, element: <Library /> },
      { path: LEARNER_DOWNLOADS, element: <Downloads /> },
      // Additional routes
      { path: LEARNER_ASSESSMENTS, element: <LearnerAssessments /> },
      { path: LEARNER_HELP, element: <HelpCenter /> },
      { path: LEARNER_MY_REVIEWS, element: <MyReviews /> },
      { path: LEARNER_PROFILE, element: <Profile /> },
      { path: LEARNER_TICKETS, element: <LearnerTickets /> },
      { path: LEARNER_TICKET_CREATE, element: <CreateTicket /> },
      { path: 'tickets/:ticketId/chat', element: <TicketChat /> },
      // Notification pages
      { path: LEARNER_NOTIFICATIONS, element: <LearnerNotificationsPage /> },
      { path: LEARNER_NOTIFICATIONS_SETTINGS, element: <LearnerNotificationSettingsPage /> },
    ],
  },

  // Auth Routes
  {
    path: '/',
    element: <AuthLayout />,
    children: [
      {
        path: AUTH_LOGIN,
        element: (
          <GuestGuard>
            <Login />
          </GuestGuard>
        )
      },
      {
        path: AUTH_REGISTER,
        element: (
          <InvitationGuard>
            <GuestGuard>
              <Register />
            </GuestGuard>
          </InvitationGuard>
        )
      },
      {
        path: AUTH_REGISTER_INVITE,
        element: (
          <InvitationGuard>
            <GuestGuard>
              <RegisterInvite />
            </GuestGuard>
          </InvitationGuard>
        )
      },
      {
        path: AUTH_FORGOT_PASSWORD,
        element: (
          <GuestGuard>
            <ForgotPassword />
          </GuestGuard>
        )
      },
      {
        path: AUTH_RESET_PASSWORD,
        element: (
          <GuestGuard>
            <ResetPassword />
          </GuestGuard>
        )
      },
      { path: AUTH_VERIFY_EMAIL, element: <VerifyEmail /> },
      { path: AUTH_HANDLE_TOKEN, element: <HandleToken /> },
      { path: AUTH_ACTIVATE_ACCOUNT, element: <ActivateAccount /> },
    ],
  },

  // LTI Deep Linking picker - opened from inside the LMS after a launch, so it
  // is standalone (no dashboard chrome) and relies on the session the launch set.
  {
    path: LTI_CONTENT_SELECT,
    element: <LtiContentSelect />
  },

  // End of an LMS-launched session - public and standalone, so it renders inside the LMS frame.
  {
    path: LTI_SIGNED_OUT,
    element: <LtiSignedOut />
  },

  // Invalid Invitation - Standalone page without AuthLayout
  {
    path: ERROR_INVALID_INVITATION,
    element: <InvalidInvitationPage type="cancelled" />
  },

  // Public Certificate Verification - No authentication required
  {
    path: `${PUBLIC_CERTIFICATE_VERIFY}/:certificateNumber`,
    element: <CertificateVerify />
  },

  // Error Routes
  {
    path: ERROR_UNAUTHORIZED,
    element: <UnauthorizedAccess />
  },
  {
    path: ERROR_ACCESS_DENIED,
    element: <AccessDenied />
  },
  {
    path: ERROR_TENANT_BLOCKED,
    element: <TenantBlocked />
  },

  // 404 Route
  {
    path: '*',
    element: (
      <ErrorPage
        title="404"
        subtitle="Oops! Page not found"
        description="The page you're looking for doesn't exist or has been moved. Please check the URL or head back to the dashboard."
        primaryButtonText="Dashboard"
        secondaryButtonText="Go back"
        primaryButtonLink="/"
      />
    )
  }
    ] // End of children array for root RouteTracker route
  } // End of root route
]);

export default function Router() {
  return (
    <ErrorBoundary>
      <RouterProvider router={router} />
    </ErrorBoundary>
  );
}
